/* ═══════════════════════════════════════════════════════════════════════
   FREE-ACCESS GRANTS

   The canonical identifier for a free-access grant is the reader's Firebase
   Auth UID — NOT their reader-document id.

   Why UID and not the reader-doc id: the access gate (`PurchaseGate`) runs as
   the signed-in reader, and the only identity it can see is `request.auth.uid`.
   Firestore rules forbid a reader from reading the `readers` collection, so the
   gate can never resolve its own reader-doc id at read time. Therefore the
   `freeAccessReaders` list stored on ebook/audiobook docs must hold auth UIDs.

   Legacy lists stored reader-doc ids (which silently never matched, so grants
   only worked when the title was £0). Those are converted by the one-off
   `scripts/migrateFreeAccessToUid.js`.
═══════════════════════════════════════════════════════════════════════ */

/**
 * The value to store in `freeAccessReaders` when granting a reader.
 * Prefers the canonical auth UID; falls back to the reader-doc id only when the
 * reader has never signed in (no UID yet). Such a fallback grant stays dormant
 * until they sign in and can be re-granted, but is never silently lost.
 */
export function grantIdForReader(reader) {
  return (reader && (reader.authUid || reader.id)) || '';
}

/**
 * Whether a loaded reader record corresponds to a stored grant id. Tolerant of
 * both the canonical UID and any not-yet-migrated reader-doc id, so grant chips
 * still render correctly during/after migration.
 */
export function readerMatchesGrant(reader, grantId) {
  if (!reader || !grantId) return false;
  return reader.authUid === grantId || reader.id === grantId;
}

/**
 * Decide whether a signed-in reader may access a piece of content for free, and
 * how that should be recorded. Shared by ebooks and audiobooks (both use the
 * same `freeAccessReaders` field).
 *
 * A reader whose UID is in `freeAccessReaders` is "granted free" — and this holds
 * EVEN WHEN the content carries a non-zero price. That is exactly what makes a
 * restricted (paid) ARC possible: set a real price, list the ARC readers, and
 * only they read for free while everyone else must buy.
 *
 * @returns {{ hasAccess: boolean, grantedFree: boolean, claimedFree: boolean }}
 */
export function resolveFreeAccess({ freeAccessReaders, uid, price }) {
  const list = Array.isArray(freeAccessReaders) ? freeAccessReaders : [];
  const grantedFree = Boolean(uid) && list.includes(uid);
  const isFree = !price || Number(price) === 0;
  return {
    hasAccess: grantedFree || isFree,
    grantedFree,                          // comped via the free-access list (any price)
    claimedFree: !grantedFree && isFree,  // took a genuinely free (£0) title
  };
}
